import type { FC } from "react";
import { useState } from "react";
import { Plus } from "lucide-react";

import { useGetConsultationsFromPatient } from "@/api/consultations";
import ProgressStep from "@/components/layout/progress-step";
import ConsultationSideModal from "@/components/modals/consultation-side-modal";
import PatientCard from "@/components/patient/patient-card";
import { Button } from "@/components/ui/button";
import { getConsultationStepLabel } from "@/types/api/consultation";
import type { Patient } from "@/types/api/patient";
import { getConsultationFields } from "@/utils/treatment";

interface LastConsultationsProps {
  patient: Patient;
  onClick: () => void;
}

const LastConsultations: FC<LastConsultationsProps> = ({
  patient,
  onClick,
}) => {
  const { data: consultations = [] } = useGetConsultationsFromPatient(
    patient.id
  );
  const [cardOpen, setCardOpen] = useState<number | null>(null);

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex justify-between items-center">
        <div className="uppercase text-gray-600 text-xs font-medium">
          Dernières consultations
        </div>
        <Button
          variant={"outline"}
          type={"button"}
          className="gap-1 whitespace-nowrap"
          onClick={onClick}
        >
          <Plus width={20} height={20} />
          {"Ajouter une consultation"}
        </Button>
      </div>
      <div className="flex gap-4">
        <div className="flex flex-col">
          {consultations.map((consultation, index) => (
            <ProgressStep
              key={`step-${consultation.id}`}
              isFirst={index === 0}
              isLast={index === consultations.length - 1}
            />
          ))}
        </div>
        <div className="flex w-full flex-col gap-5">
          {consultations?.map((consultation) => (
            <PatientCard
              key={`consultation-${consultation.id}`}
              reason={getConsultationStepLabel(consultation.consultationStep)}
              date={new Date(consultation.startDate)}
              comment={consultation.comment}
              onClick={() => setCardOpen(consultation.id)}
              extraFields={getConsultationFields(consultation)}
            />
          ))}
        </div>
      </div>
      <ConsultationSideModal
        open={cardOpen !== null}
        onOpenChange={() => setCardOpen(null)}
        consultation={consultations.find(
          (consultation) => consultation.id === cardOpen
        )}
        patientId={patient.id}
      />
    </div>
  );
};

export default LastConsultations;
